/* eslint-disable */
// @ts-nocheck

import Highcharts from "highcharts";
import debounce from "lodash.debounce";

const MAX_DISPLAYED_MARKERS = 200;
export const MARKERS_VISIBLE_EVENT = "markers-visible";

function findLastIndex<T>(
  array: Array<T>,
  predicate: (value: T, index: number, obj: T[]) => boolean,
): number {
  let l = array.length;
  while (l--) {
    if (predicate(array[l], l, array)) return l;
  }
  return -1;
}

function pick(...args: unknown[]) {
  let length = args.length;
  for (let i = 0; i < length; i++) {
    let arg = args[i];
    if (typeof arg !== "undefined" && arg !== null) {
      return arg;
    }
  }
}

const debouncedSeriesRerender = debounce((series: Highcharts.Series) => {
  // check that serie is still "operational" (fix Highcharts crash)
  if (series.options) series.render();
}, 300);

const axisMarkersHidden = (axis: Highcharts.Axis) => {
  if (axis.options.type !== "datetime") return false;
  const { min, max } = axis;
  const markerSeries = axis.series.filter(
    s =>
      !s.options.isInternal &&
      (s.userOptions.visible === true || s.userOptions.visible === undefined),
  );

  // not perfect but good enough?
  const memoizationKey = JSON.stringify({
    min,
    max,
    points: markerSeries.map(s => s.xData.length),
  });
  if (axis.__markersHiddenMemoizationKey === memoizationKey) return axis.__markersHidden;
  axis.__markersHiddenMemoizationKey = memoizationKey;
  axis.__markersHidden =
    markerSeries.reduce((totalNbMarkerUsed, s) => {
      const startIndex = min == undefined ? s.xData[0] : s.xData.findIndex(d => d >= min);
      const endIndex = findLastIndex(s.xData, d => d <= max);

      const isMarkerVisible = m =>
        m.marker?.enabled !== false &&
        m.x >= min &&
        m.x <= max &&
        m.y !== null &&
        m.y !== undefined &&
        m.y !== 0 &&
        m.marker?.symbol !== "empty";

      const markerUsed = s.userOptions.data.filter(isMarkerVisible);

      const nbMarkerUsed =
        startIndex != undefined && endIndex != undefined && startIndex !== -1 && endIndex !== -1
          ? totalNbMarkerUsed + markerUsed.length
          : totalNbMarkerUsed + 0;

      return nbMarkerUsed;
    }, 0) > MAX_DISPLAYED_MARKERS;

  Highcharts.fireEvent(axis, MARKERS_VISIBLE_EVENT, { value: !axis.__markersHidden });

  return axis.__markersHidden;
};

export default function improveDrawPoints(H: typeof Highcharts) {
  H.addEvent(H.Point, "select", function (this: Highcharts.Point) {
    this.selected = true;
    debouncedSeriesRerender(this.series);
  });
  H.addEvent(H.Point, "unselect", function (this: Highcharts.Point) {
    debouncedSeriesRerender(this.series);
  });
  H.wrap(H.Series.prototype, "drawPoints", function (this: Highcharts.Series) {
    if (
      this.options.isInternal ||
      (this.options.marker?.enabled === false && !this._hasPointMarkers)
    )
      return;
    let series = this,
      points = series.points,
      chart = series.chart,
      i,
      point,
      graphic,
      verb,
      options = series.options,
      seriesMarkerOptions = options.marker,
      pointMarkerOptions,
      hasPointMarker,
      markerGroup = series[series.specialGroup] || series.markerGroup,
      xAxis = series.xAxis,
      markerAttribs;

    const markersHidden = axisMarkersHidden(this.xAxis);
    points = points.filter(p => {
      pointMarkerOptions = p.marker || {};
      const showMarker = p.marker?.enabled ?? (!markersHidden || p.selected);
      const toDraw = showMarker && !p.isNull && p.visible !== false;

      if (!toDraw && p.graphic) {
        p.graphic = p.graphic.destroy();
      }
      return toDraw;
    });
    for (i = 0; i < points.length; i++) {
      point = points[i];
      graphic = point.graphic;
      verb = graphic ? "animate" : "attr";
      pointMarkerOptions = point.marker || {};
      hasPointMarker = Boolean(point.marker);
      // Shortcuts
      const symbol = pick(pointMarkerOptions.symbol, series.symbol);
      markerAttribs = series.markerAttribs(point, point.selected && "select");
      // Set starting position for point sliding animation.
      if (series.enabledDataSorting) {
        point.startXPos = xAxis.reversed ? -markerAttribs.width : xAxis.width;
      }
      const isInside = point.isInside !== false;
      if (graphic) {
        graphic[isInside ? "show" : "hide"](isInside).animate(markerAttribs);
      } else if (isInside && (markerAttribs.width > 0 || point.hasImage)) {
        point.graphic = graphic = chart.renderer
          .symbol(
            symbol,
            markerAttribs.x,
            markerAttribs.y,
            markerAttribs.width,
            markerAttribs.height,
            hasPointMarker ? pointMarkerOptions : seriesMarkerOptions,
          )
          .add(markerGroup);
        // Sliding animation for new points
        if (series.enabledDataSorting && chart.hasRendered) {
          graphic.attr({
            x: point.startXPos,
          });
          verb = "animate";
        }
      }
      if (graphic && verb === "animate") {
        // update
        // Since the marker group isn't clipped, each
        // individual marker must be toggled
        graphic[isInside ? "show" : "hide"](isInside).animate(markerAttribs);
      }
      // Presentational attributes
      if (graphic && !chart.styledMode) {
        graphic[verb](series.pointAttribs(point, point.selected && "select"));
      }
      if (graphic) {
        graphic.addClass(point.getClassName(), true);
      }
    }
  });
}
